import { /* useEffect, */ useMemo, useState } from 'react';
import { useCommunityUnpriced } from '../api/communityUnpriced';
import {
  useCostedLines,
  // useHideLaborLine,
  // useUnhideLaborLine,
} from '../api/lines';
import { formatSupplierDate } from '../api/suppliers';
// import { Button } from '../components/ui/Button';
import {
  // LineOverrideActionButtons,
  LineOverrideClearConfirm,
  // LineOverrideEditModal,
  LineOverrideHistoryModal,
  LineQtyPriceEditModal,
  LineQtyPriceOverrideActions,
  useLineOverrideModalState,
} from '../components/mto/LineOverrideModals';
import { PricingSearchableTable } from '../components/pricing/PricingSearchableTable';
import { Select } from '../components/ui/Select';
import type { ColumnDef } from '@tanstack/react-table';
import { formatMoney } from '../lib/money';
import page from './Page.module.css';

type MtoRow = { id?: unknown; name?: unknown };
export type PricingSection =
  | 'laborDefaults'
  | 'unpricedItems'
  // | 'mtoPriceOverrides'
  | 'modelOverrides';

export function CommunityLaborAdvancedSections({
  communityId,
  mtos,
  section,
  bidLocked,
  toast,
}: {
  communityId: string;
  mtos: MtoRow[];
  section: PricingSection;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  if (section === 'unpricedItems') {
    return <UnpricedItemsSection communityId={communityId} bidLocked={bidLocked} toast={toast} />;
  }
  // if (section === 'mtoPriceOverrides') {
  //   return <MtoPriceOverridesSection communityId={communityId} mtos={mtos} bidLocked={bidLocked} toast={toast} />;
  // }
  return <ModelOverridesSection communityId={communityId} mtos={mtos} bidLocked={bidLocked} toast={toast} />;
}

function UnpricedItemsSection({
  communityId,
  bidLocked: _bidLocked,
  toast: _toast,
}: {
  communityId: string;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const { data: unpriced, isLoading } = useCommunityUnpriced(communityId);

  const materialRows = useMemo(
    () => (unpriced?.defaultItems ?? []) as Record<string, unknown>[],
    [unpriced]
  );

  const missingPriceSearchKeys = useMemo(
    () => ['itemName', 'phase', 'supplierCategory', 'supplier'] as const,
    []
  );

  const materialCols: ColumnDef<Record<string, unknown>>[] = useMemo(
    () => [
      { header: 'Item', accessorKey: 'itemName' },
      { header: 'Phase', accessorKey: 'phase' },
      { header: 'Supplier Category', accessorKey: 'supplierCategory' },
      { header: 'Supplier', accessorKey: 'supplier' },
    ],
    []
  );

  return (
    <div className={page.card}>
      <h3 className={page.title} style={{ fontSize: 16, margin: '0 0 var(--space-md)' }}>
        Missing Supplier Prices
      </h3>
      <p className={page.muted}>
        Material, equipment, and misc items that still need a price on the assigned supplier sheet.
        Labor items are managed on the Community Labor Rates tab.
      </p>
      {isLoading ? <p className={page.muted}>Loading missing supplier prices…</p> : null}
      {materialRows.length ? (
        <PricingSearchableTable
          data={materialRows}
          columns={materialCols}
          searchKeys={missingPriceSearchKeys}
          searchPlaceholder="Item, phase, supplier category, or supplier"
        />
      ) : !isLoading ? (
        <p className={page.muted}>No material items missing supplier prices — supplier coverage may be complete.</p>
      ) : null}
    </div>
  );
}

/* Temporarily hidden — restore with Per-Line Rate Overrides tab
function MtoPriceOverridesSection({
  communityId,
  mtos,
  bidLocked,
  toast,
}: {
  communityId: string;
  mtos: MtoRow[];
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const { data: unpriced, isLoading: unpricedLoading } = useCommunityUnpriced(communityId);
  const [selectedMto, setSelectedMto] = useState(() => String(mtos[0]?.id ?? ''));
  const modal = useLineOverrideModalState();
  const hideLine = useHideLaborLine(selectedMto || undefined, communityId);
  const unhideLine = useUnhideLaborLine(selectedMto || undefined, communityId);
  const { data: costed, isLoading: costedLoading } = useCostedLines(selectedMto || undefined);

  useEffect(() => {
    if (!mtos.length) return;
    setSelectedMto((prev) => prev || String(mtos[0]?.id ?? ''));
  }, [mtos]);

  const costedByLineId = useMemo(() => {
    const map = new Map<number, Record<string, unknown>>();
    for (const row of (costed?.lines ?? []) as Record<string, unknown>[]) {
      const id = Number(row.id);
      if (id) map.set(id, row);
    }
    return map;
  }, [costed]);

  const lineRows = useMemo(() => {
    const items = unpriced?.items ?? [];
    return items.filter(
      (r) => String(r.type) === 'mto' && r.isLabor === true
    ) as Record<string, unknown>[];
  }, [unpriced]);

  const rowsForSelectedMto = useMemo(
    () => (selectedMto ? lineRows.filter((r) => String(r.mtoId) === selectedMto) : []),
    [lineRows, selectedMto]
  );

  const perLineSearchKeys = useMemo(
    () => ['itemId', 'qty'] as const,
    []
  );

  const lineCols: ColumnDef<Record<string, unknown>>[] = useMemo(
    () => [
      { header: 'Item', accessorKey: 'itemId' },
      {
        header: 'Qty',
        accessorKey: 'qty',
        meta: { align: 'right' },
      },
      {
        header: 'Current Line Rate',
        cell: ({ row }) => {
          const lineId = Number(row.original.lineId);
          const costedRow = costedByLineId.get(lineId);
          const val = costedRow?.manual_cost;
          return val != null ? formatMoney(Number(val)) : '—';
        },
        meta: { align: 'right' },
      },
      {
        header: 'Effective Date',
        cell: ({ row }) => {
          const lineId = Number(row.original.lineId);
          const costedRow = costedByLineId.get(lineId);
          return formatSupplierDate(costedRow?.manual_cost_effective_date as string | undefined);
        },
      },
      {
        header: 'Hidden',
        accessorFn: (r) => (r.hidden ? 'Yes' : ''),
      },
      {
        id: 'override-actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const lineId = Number(row.original.lineId);
          const itemLabel = String(row.original.itemId ?? '');
          const costedRow = costedByLineId.get(lineId);
          const hidden = !!row.original.hidden;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <LineOverrideActionButtons
                bidLocked={bidLocked}
                hasValue={costedRow?.manual_cost != null}
                onEdit={() =>
                  modal.setEdit({
                    lineId,
                    kind: 'manual_cost',
                    itemLabel,
                    currentValue:
                      costedRow?.manual_cost != null ? Number(costedRow.manual_cost) : null,
                  })
                }
                onHistory={() => modal.setHistory({ lineId, kind: 'manual_cost', itemLabel })}
                onClear={() => modal.setPendingClear({ lineId, kind: 'manual_cost' })}
              />
              {hidden ? (
                <Button
                  type="button"
                  size="sm"
                  variant="success"
                  disabled={bidLocked}
                  onClick={() =>
                    unhideLine.mutate(lineId, {
                      onSuccess: () => toast('Line restored'),
                      onError: (e) => toast(e instanceof Error ? e.message : 'Failed', true),
                    })
                  }
                >
                  Unhide
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="success"
                  disabled={bidLocked}
                  onClick={() =>
                    hideLine.mutate(lineId, {
                      onSuccess: () => toast('Line excluded from bid'),
                      onError: (e) => toast(e instanceof Error ? e.message : 'Failed', true),
                    })
                  }
                >
                  Hide
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [bidLocked, costedByLineId, hideLine, modal, toast, unhideLine]
  );

  const isLoading = unpricedLoading || costedLoading;

  return (
    <div className={page.card}>
      <h3 className={page.title} style={{ fontSize: 16 }}>
        Per-Line Rate Overrides
      </h3>
      <p className={page.muted}>
        For lines with no supplier price, set a unit price for this floorplan only (replaces the community
        default). Use Hide to exclude a line from the bid.
      </p>
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <Select label="Model" value={selectedMto} onChange={(e) => setSelectedMto(e.target.value)}>
          <option value="">Select a model…</option>
          {(mtos ?? []).map((m) => (
            <option key={String(m.id)} value={String(m.id)}>
              {String(m.name)}
            </option>
          ))}
        </Select>
      </div>
      {!selectedMto ? (
        <p className={page.muted}>Select a model to view and edit per-line rates.</p>
      ) : isLoading ? (
        <p className={page.muted}>Loading lines…</p>
      ) : !rowsForSelectedMto.length ? (
        <p className={page.muted}>No lines missing supplier price for this model.</p>
      ) : (
        <PricingSearchableTable
          data={rowsForSelectedMto}
          columns={lineCols}
          searchKeys={perLineSearchKeys}
          searchPlaceholder="Item or qty"
        />
      )}

      {modal.edit ? (
        <LineOverrideEditModal
          key={`${modal.edit.lineId}-${modal.edit.kind}`}
          open
          kind={modal.edit.kind}
          lineId={modal.edit.lineId}
          itemLabel={modal.edit.itemLabel}
          currentValue={modal.edit.currentValue}
          mtoId={selectedMto}
          communityId={communityId}
          bidLocked={bidLocked}
          onClose={() => modal.setEdit(null)}
          onSaved={() => {}}
        />
      ) : null}
      {modal.history ? (
        <LineOverrideHistoryModal
          open
          kind={modal.history.kind}
          lineId={modal.history.lineId}
          itemLabel={modal.history.itemLabel}
          onClose={() => modal.setHistory(null)}
        />
      ) : null}
      <LineOverrideClearConfirm
        pending={modal.pendingClear}
        mtoId={selectedMto}
        communityId={communityId}
        onCancel={() => modal.setPendingClear(null)}
        onCleared={() => {}}
      />
    </div>
  );
}
*/

function ModelOverridesSection({
  communityId,
  mtos,
  bidLocked,
  toast: _toast,
}: {
  communityId: string;
  mtos: MtoRow[];
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const [mtoId, setMtoId] = useState(String(mtos[0]?.id ?? ''));
  const modal = useLineOverrideModalState();
  const { data: costed, isLoading } = useCostedLines(mtoId || undefined);

  const rows = useMemo(() => {
    const lines = (costed?.lines ?? []) as Record<string, unknown>[];
    return lines.filter((l) => l.type !== 'manual');
  }, [costed]);

  const forceLineSearchKeys = useMemo(
    () => ['item_id', 'qty', 'qty_override', 'price_override', 'source'] as const,
    []
  );

  const cols: ColumnDef<Record<string, unknown>>[] = useMemo(
    () => [
      { header: 'Item', accessorKey: 'item_id' },
      { header: 'MTO Qty', accessorKey: 'qty', meta: { align: 'right' } },
      {
        header: 'Current Override Qty',
        cell: ({ row }) =>
          row.original.qty_override != null ? String(row.original.qty_override) : '—',
        meta: { align: 'right' },
      },
      {
        header: 'Qty Effective Date',
        cell: ({ row }) => formatSupplierDate(row.original.qty_override_effective_date as string),
      },
      {
        header: 'Current Forced Price',
        cell: ({ row }) =>
          row.original.price_override != null
            ? formatMoney(Number(row.original.price_override))
            : '—',
        meta: { align: 'right' },
      },
      {
        header: 'Price Effective Date',
        cell: ({ row }) => formatSupplierDate(row.original.price_override_effective_date as string),
      },
      { header: 'Effective Qty', accessorKey: 'effective_qty', meta: { align: 'right' } },
      {
        header: 'Resolved Unit Price',
        cell: ({ row }) =>
          row.original.unit_price != null ? formatMoney(Number(row.original.unit_price)) : '—',
        meta: { align: 'right' },
      },
      {
        header: 'Line $',
        cell: ({ row }) =>
          row.original.line_total != null ? formatMoney(Number(row.original.line_total)) : '—',
        meta: { align: 'right' },
      },
      { header: 'Price Source', accessorKey: 'source' },
      {
        id: 'override-actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const lineId = Number(row.original.id);
          const itemLabel = String(row.original.item_id ?? '');
          return (
            <LineQtyPriceOverrideActions
              bidLocked={bidLocked}
              hasQtyOverride={row.original.qty_override != null}
              hasPriceOverride={row.original.price_override != null}
              onEdit={() =>
                modal.setQtyPriceEdit({
                  lineId,
                  itemLabel,
                  mtoQty: row.original.qty != null ? Number(row.original.qty) : null,
                  currentQtyOverride:
                    row.original.qty_override != null ? Number(row.original.qty_override) : null,
                  currentPriceOverride:
                    row.original.price_override != null
                      ? Number(row.original.price_override)
                      : null,
                })
              }
              onQtyHistory={() => modal.setHistory({ lineId, kind: 'qty', itemLabel })}
              onPriceHistory={() => modal.setHistory({ lineId, kind: 'price', itemLabel })}
              onClearQty={() => modal.setPendingClear({ lineId, kind: 'qty' })}
              onClearPrice={() => modal.setPendingClear({ lineId, kind: 'price' })}
            />
          );
        },
      },
    ],
    [bidLocked, modal]
  );

  return (
    <div className={page.card}>
      <h3 className={page.title} style={{ fontSize: 16 }}>
        Force Line Price and Qty
      </h3>
      <p className={page.muted}>
        Override quantity or unit price for any line, including when supplier pricing exists. Use Edit to
        change qty and/or forced price in one step; Clear removes all history for that override.
      </p>
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <Select label="MTO File" value={mtoId} onChange={(e) => setMtoId(e.target.value)}>
          <option value="">Select…</option>
          {(mtos ?? []).map((m) => (
            <option key={String(m.id)} value={String(m.id)}>
              {String(m.name)}
            </option>
          ))}
        </Select>
      </div>
      {isLoading ? <p className={page.muted}>Loading costed lines…</p> : null}
      {mtoId && rows.length ? (
        <PricingSearchableTable
          data={rows}
          columns={cols}
          searchKeys={forceLineSearchKeys}
          searchPlaceholder="Item, qty, price, or source"
        />
      ) : mtoId ? (
        <p className={page.muted}>No lines for this MTO.</p>
      ) : (
        <p className={page.muted}>Select a model to edit line overrides.</p>
      )}

      {modal.qtyPriceEdit ? (
        <LineQtyPriceEditModal
          key={modal.qtyPriceEdit.lineId}
          open
          lineId={modal.qtyPriceEdit.lineId}
          itemLabel={modal.qtyPriceEdit.itemLabel}
          mtoQty={modal.qtyPriceEdit.mtoQty}
          currentQtyOverride={modal.qtyPriceEdit.currentQtyOverride}
          currentPriceOverride={modal.qtyPriceEdit.currentPriceOverride}
          mtoId={mtoId}
          communityId={communityId}
          bidLocked={bidLocked}
          onClose={() => modal.setQtyPriceEdit(null)}
          onSaved={() => {}}
        />
      ) : null}
      {modal.history ? (
        <LineOverrideHistoryModal
          open
          kind={modal.history.kind}
          lineId={modal.history.lineId}
          itemLabel={modal.history.itemLabel}
          onClose={() => modal.setHistory(null)}
        />
      ) : null}
      <LineOverrideClearConfirm
        pending={modal.pendingClear}
        mtoId={mtoId}
        communityId={communityId}
        onCancel={() => modal.setPendingClear(null)}
        onCleared={() => {}}
      />
    </div>
  );
}
